/**
 * The app-usage timing state machine, shared by all three apps.
 *
 * Deliberately platform-agnostic: no `document`, no `react-native`, no fetch.
 * The clock, the timers and the transport are all injected. That is what lets
 * the same file run in a CRA bundle, in Hermes, and under `node --test`.
 *
 * The model: while the app is visible the tracker posts a beat roughly every
 * `intervalMs`. The server derives duration from the gaps between beats, so a
 * dropped beat costs accuracy but never correctness, and the tracker never
 * needs to report an end — which is the point, since the events that end a
 * session (crash, force-quit, closed lid) are exactly the ones a client cannot
 * report. See cloud-sql/2026-09-11-app-usage-tracking.sql.
 */

/**
 * Session id generation.
 *
 * The id only has to be unique and unguessable enough that two concurrent
 * sessions never collide; it carries no meaning and is never shown to anyone.
 *
 * crypto.randomUUID is not available everywhere this runs -- React Native's
 * Hermes engine does not expose it reliably, and neither does a browser on an
 * insecure origin -- so the fallback is mandatory rather than defensive. It is
 * NOT cryptographically strong, which is why the server treats a session id as
 * a claim to be checked (it refuses a beat against a session belonging to a
 * different email) rather than as a credential.
 *
 * Output must satisfy the CHECK on app_usage_sessions.session_id:
 * ^[A-Za-z0-9_-]{16,64}$ -- hyphens are allowed, so a plain UUID passes.
 *
 * Defined here rather than in its own module on purpose: this package is
 * consumed both by bundlers (extensionless imports) and by node --test's
 * type-stripping loader (which requires extensions), and the two cannot agree
 * on a relative specifier under TypeScript 4.9. Keeping the package to
 * single-file runtime modules sidesteps that entirely.
 */
export function newSessionId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();

  // v4-shaped, from Math.random. 36 chars, within the CHECK's range.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export type UsageApp = 'runner' | 'coach' | 'mobile';
export type UsagePlatform = 'web' | 'ios' | 'android';

export interface UsageBeat {
  session_id: string;
  app: UsageApp;
  platform: UsagePlatform;
  page_key: string;
  app_version?: string | null;
  is_page_entry?: boolean;
  is_new_login?: boolean;
}

export interface UsageTrackerOptions {
  app: UsageApp;
  platform: UsagePlatform;
  appVersion?: string | null;
  /** Fire-and-forget transport. Its result and its failures are both ignored. */
  send: (beat: UsageBeat) => void | Promise<unknown>;
  /** How often to beat while active. The server's 90s gap cap assumes ~60s. */
  intervalMs?: number;
  /**
   * A pause longer than this ends the session: the next resume starts a fresh
   * one. Without it, a laptop reopened on Thursday would extend Monday's
   * session and report a three-day visit.
   */
  idleMs?: number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** Reads/writes the session id across reloads. Omit for in-memory only. */
  store?: UsageSessionStore;
}

/** Persistence for the session id, so an in-tab reload continues the session. */
export interface UsageSessionStore {
  read(): { id: string; email: string } | null;
  write(value: { id: string; email: string }): void;
  clear(): void;
}

export interface UsageTracker {
  start(pageKey: string, opts?: { email?: string; isNewLogin?: boolean }): void;
  setPage(pageKey: string): void;
  pause(): void;
  resume(): void;
  stop(): void;
  getSessionId(): string | null;
  isRunning(): boolean;
}

export function createUsageTracker(options: UsageTrackerOptions): UsageTracker {
  const {
    app,
    platform,
    appVersion = null,
    send,
    intervalMs = 60_000,
    idleMs = 30 * 60_000,
    now = Date.now,
    setTimer = ((fn: () => void, ms: number) => setInterval(fn, ms)) as NonNullable<UsageTrackerOptions['setTimer']>,
    clearTimer = ((h: unknown) => clearInterval(h as ReturnType<typeof setInterval>)) as NonNullable<UsageTrackerOptions['clearTimer']>,
    store,
  } = options;

  let sessionId: string | null = null;
  let email: string | null = null;
  let pageKey = 'unknown';
  let handle: unknown = null;
  let pausedAt: number | null = null;

  function beat(extra: Partial<UsageBeat> = {}): void {
    if (!sessionId) return;
    try {
      // Never await: a slow or failed beat must not stall the caller, and a
      // rejected promise must not surface as an unhandled rejection.
      void Promise.resolve(
        send({
          session_id: sessionId,
          app,
          platform,
          page_key: pageKey,
          app_version: appVersion,
          ...extra,
        })
      ).catch(() => {});
    } catch {
      // A transport that throws synchronously is still just a lost beat.
    }
  }

  function arm(): void {
    disarm();
    handle = setTimer(() => beat(), intervalMs);
  }

  function disarm(): void {
    if (handle !== null) {
      clearTimer(handle);
      handle = null;
    }
  }

  function begin(key: string, isNewLogin: boolean): void {
    sessionId = newSessionId();
    pageKey = key;
    pausedAt = null;
    if (store && email) store.write({ id: sessionId, email });
    // The opening beat is flagged as a page entry so the first page of a
    // session counts as a visit.
    beat({ is_page_entry: true, is_new_login: isNewLogin });
    arm();
  }

  return {
    start(key, opts = {}) {
      const nextEmail = opts.email ?? null;

      // A stored session continues only if it belongs to the same account. An
      // account switch in the same tab must not inherit the previous user's
      // session — the server would refuse those beats anyway.
      const stored = store?.read() ?? null;
      if (stored && nextEmail && stored.email === nextEmail) {
        sessionId = stored.id;
        email = nextEmail;
        pageKey = key;
        pausedAt = null;
        beat({ is_page_entry: true });
        arm();
        return;
      }

      email = nextEmail;
      begin(key, opts.isNewLogin === true);
    },

    setPage(key) {
      if (!sessionId || key === pageKey) return;
      pageKey = key;
      // Beat at once rather than waiting out the interval: otherwise a page
      // visited briefly would never be recorded at all.
      beat({ is_page_entry: true });
      arm(); // reset the phase so the next beat is a full interval away
    },

    pause() {
      if (!sessionId || pausedAt !== null) return;
      disarm();
      pausedAt = now();
    },

    resume() {
      if (!sessionId || pausedAt === null) return;
      const away = now() - pausedAt;
      pausedAt = null;

      if (away > idleMs) {
        // Too long to be the same visit. Start a fresh session rather than
        // letting the old one absorb the whole gap.
        store?.clear();
        begin(pageKey, false);
        return;
      }
      beat();
      arm();
    },

    stop() {
      disarm();
      sessionId = null;
      pausedAt = null;
      store?.clear();
    },

    getSessionId() {
      return sessionId;
    },

    isRunning() {
      return sessionId !== null && handle !== null;
    },
  };
}
